import { ApolloClient, Reference, StoreObject, gql } from '@apollo/client';
import {
  AddChecklistMutation,
  GetMyChecklistsDocument,
  GetMyChecklistsQuery,
  GetMyChecklistsQueryVariables,
} from '@/generated/codegen';

/** Fixed pagination for the machinist's own checklists list: the table loads
 * once and paginates client-side (like MyMachineNoticesTable), which keeps
 * these variables stable — the submit flow's manual cache merge (below)
 * needs to target the exact same cache entry the table reads from. */
// FilterPagination.current_page is 0-based (lib/kysely/pagination.ts): 1 would
// skip the first page and the list would come back empty.
export const MY_CHECKLISTS_PAGINATION = { current_page: 0, per_page: 200 };

export const CHECKLIST_LIST_FIELDS = gql`
  fragment ChecklistListFields on Checklist {
    id
    serial_number
    machine_id
    template_id
    template_title
    version_number
    completed_at
    completed_date
    pass_count
    fail_count
    na_count
    created_at
    created_by {
      id
      first_name
      last_name
    }
    machine {
      id
      name
    }
  }
`;

/** No $context — the PWA has none (a superroot's role has no company); the
 * server authorizes against the actor's full scope when it's omitted. */
export const GET_MY_CHECKLISTS = gql`
  query GetMyChecklists(
    $filter: FilterChecklists
    $pagination: FilterPagination
  ) {
    getChecklists(filter: $filter, pagination: $pagination) {
      page_info {
        total_pages
        current_page
        total_items
        items_per_page
      }
      items {
        ...ChecklistListFields
      }
    }
  }
  ${CHECKLIST_LIST_FIELDS}
`;

/**
 * Prepends a just-submitted checklist to the cached "my checklists" list —
 * done by hand instead of via `useOfflineMutation`'s `queries` cache-update
 * mechanism, which assumes every mutation response is wrapped as `{ item }`
 * (see cacheUpdaterAdd in @bilderit/offline-link); `addChecklist` returns a
 * bare `Checklist`, so that mechanism would throw reading `.item.id` off
 * `undefined`. This mirrors what it would have done, correctly.
 *
 * When the list was never loaded on this device (the preload only fetches
 * machines and availability), the checklist is written as a list of its own:
 * the operator who submits offline then sees it as pending instead of a list
 * with no trace of it. The list query reads `cache-and-network`, so the
 * server's history replaces that stand-in as soon as the request goes through
 * (it is held until connectivity returns).
 */
export const mergeChecklistIntoMyChecklistsCache = (
  client: ApolloClient<object>,
  variables: GetMyChecklistsQueryVariables,
  checklist: NonNullable<AddChecklistMutation['addChecklist']>
): void => {
  try {
    const existing = client.cache.readQuery<
      GetMyChecklistsQuery,
      GetMyChecklistsQueryVariables
    >({ query: GetMyChecklistsDocument, variables });
    const items = [
      checklist,
      ...(existing?.getChecklists.items ?? []).filter(
        (item) => item.id !== checklist.id
      ),
    ];

    client.cache.writeQuery<
      GetMyChecklistsQuery,
      GetMyChecklistsQueryVariables
    >({
      query: GetMyChecklistsDocument,
      variables,
      data: {
        __typename: 'Query',
        getChecklists: {
          __typename: 'ChecklistsResponse',
          page_info: existing?.getChecklists.page_info ?? {
            __typename: 'PageInfo',
            total_pages: 1,
            current_page: variables.pagination?.current_page ?? 0,
            total_items: items.length,
            items_per_page:
              variables.pagination?.per_page ??
              MY_CHECKLISTS_PAGINATION.per_page,
          },
          items,
        },
      },
    });
  } catch (error) {
    console.error('Error merging checklist into the list cache:', error);
  }
};

/**
 * Takes a checklist out of the cache: out of every cached `getChecklists` list
 * (whatever its variables) and its `Checklist` entity evicted. For a pending
 * row the operator discards from the failed-sends notice — its send will never
 * reach the server, so nothing else would ever remove it.
 */
export const removeChecklistFromCache = (
  client: ApolloClient<object>,
  checklistId: string
): void => {
  client.cache.modify<{
    getChecklists: StoreObject & { items?: readonly Reference[] };
  }>({
    fields: {
      getChecklists(list, { readField, isReference }) {
        // The list is stored inline, and a response without items (the
        // query's other variant) has nothing to remove.
        if (isReference(list) || !list.items) return list;
        const items = list.items.filter(
          (item) => readField<string>('id', item) !== checklistId
        );
        return items.length === list.items.length ? list : { ...list, items };
      },
    },
  });
  const entityId = client.cache.identify({
    __typename: 'Checklist',
    id: checklistId,
  });
  if (entityId) client.cache.evict({ id: entityId });
  client.cache.gc();
};
